import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Stethoscope, Mic, Square, Send, Loader2, Play, Pause, Crown, Sparkles,
  CalendarCheck, CheckCircle2, Circle, Plus, Trash2, ChevronRight, ArrowLeft, Bell, Heart, RefreshCw,
} from "lucide-react";
import Layout from "@/components/Layout";
import { api, vibrate } from "@/lib/api";

const blobToBase64 = (blob) =>
  new Promise((res) => {
    const r = new FileReader();
    r.onloadend = () => res(String(r.result).split(",")[1]);
    r.readAsDataURL(blob);
  });

// ---------------- Voice consultation ----------------

function Consultation({ goToPlans }) {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [playingIdx, setPlayingIdx] = useState(null);
  const [relations, setRelations] = useState([]);
  const [cta, setCta] = useState(null); // {relationshipId} when the Doc offers a plan
  const [ctaBusy, setCtaBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Start flow: choose to talk about a tracked contact or a fresh topic.
  const [started, setStarted] = useState(false);
  const [mode, setMode] = useState(null); // "contact" | "topic"
  const [relId, setRelId] = useState(null);
  const [relName, setRelName] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startPicker, setStartPicker] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/relationships").then((r) => setRelations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending, cta]);

  const playAudioB64 = (idx, b64) => {
    if (!b64) return;
    if (playingIdx === idx) { audioRef.current?.pause(); setPlayingIdx(null); return; }
    audioRef.current?.pause();
    const a = new Audio(`data:audio/mp3;base64,${b64}`);
    audioRef.current = a;
    a.onended = () => setPlayingIdx(null);
    a.play().catch(() => {});
    setPlayingIdx(idx);
  };

  const startContact = async (r) => {
    vibrate(); setStarting(true); setStartPicker(false);
    try {
      const { data } = await api.post("/doctor/consult/start", { relationship_id: r.relationship_id, mode: "contact" });
      setSessionId(data.session_id);
      setRelId(r.relationship_id); setRelName(r.contact_name); setMode("contact");
      setMessages([{ role: "doc", text: data.reply_text, audio: data.reply_audio_base64 }]);
      setStarted(true);
      if (data.offer_plan) setCta({ relationshipId: r.relationship_id });
      if (data.reply_audio_base64) setTimeout(() => playAudioB64(0, data.reply_audio_base64), 250);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer la consultation");
    } finally { setStarting(false); }
  };

  const startTopic = async () => {
    vibrate(); setStarting(true);
    try {
      const { data } = await api.post("/doctor/consult/start", { mode: "topic" });
      setSessionId(data.session_id); setMode("topic"); setRelId(null); setRelName(null);
      setMessages([]); setStarted(true);
    } catch (e) {
      // fall back to a local session; consult() will create one on first message
      setSessionId(null); setMode("topic"); setRelId(null); setMessages([]); setStarted(true);
    } finally { setStarting(false); }
  };

  const startOver = () => {
    vibrate(); audioRef.current?.pause(); setPlayingIdx(null);
    setStarted(false); setMode(null); setRelId(null); setRelName(null);
    setSessionId(null); setMessages([]); setCta(null); setText("");
  };

  const send = async ({ audio_base64, mimetype, textMsg }) => {
    setSending(true);
    setCta(null);
    if (textMsg) setMessages((m) => [...m, { role: "user", text: textMsg }]);
    else setMessages((m) => [...m, { role: "user", text: "🎙️ …", pending: true }]);
    try {
      const { data } = await api.post("/doctor/consult", {
        session_id: sessionId, relationship_id: relId, text: textMsg, audio_base64, mimetype,
      });
      setSessionId(data.session_id);
      setMessages((m) => {
        const clean = m.filter((x) => !x.pending);
        if (!textMsg) clean.push({ role: "user", text: data.transcript });
        clean.push({ role: "doc", text: data.reply_text, audio: data.reply_audio_base64 });
        return clean;
      });
      if (data.offer_plan) setCta({ relationshipId: relId || data.relationship_id || null });
    } catch (e) {
      setMessages((m) => m.filter((x) => !x.pending));
      toast.error(e?.response?.data?.detail || "Le Doc n'a pas pu répondre");
    } finally {
      setSending(false);
    }
  };

  const createPlan = async (rid) => {
    if (!rid) { setPickerOpen(true); return; }
    setCtaBusy(true); vibrate();
    try {
      await api.post("/doctor/plan", { relationship_id: rid });
      toast.success("Plan d'action créé 💜");
      setCta(null); setPickerOpen(false);
      goToPlans?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    } finally { setCtaBusy(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        const b64 = await blobToBase64(blob);
        send({ audio_base64: b64, mimetype: mr.mimeType || "audio/webm" });
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      vibrate();
    } catch (e) {
      toast.error("Micro inaccessible. Autorise l'accès au micro.");
    }
  };

  const stopRec = () => {
    vibrate();
    mediaRef.current?.stop();
    setRecording(false);
  };

  const playAudio = (idx, b64) => playAudioB64(idx, b64);

  const submitText = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setText("");
    send({ textMsg: t });
  };

  // ---------- Start chooser (before the conversation begins) ----------
  if (!started) {
    return (
      <div data-testid="consult-start" className="py-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-pink-500/15 flex items-center justify-center text-pink-400 mx-auto mb-4">
            <Stethoscope size={30} />
          </div>
          <h3 className="font-display text-xl font-black">De quoi veux-tu parler ?</h3>
          <p className="text-sm text-zinc-500 mt-2 max-w-xs mx-auto">
            Choisis un contact suivi (le Doc démarre par une analyse) ou lance un nouveau sujet.
          </p>
        </div>

        <button
          data-testid="consult-start-contact"
          onClick={() => { vibrate(); setStartPicker(true); }}
          disabled={starting}
          className="w-full flex items-center gap-3 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-pink-500/40 transition-colors p-4 mb-3 text-left disabled:opacity-60"
        >
          <div className="w-11 h-11 rounded-xl bg-pink-500/15 flex items-center justify-center text-pink-400 shrink-0">
            <Heart size={20} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">Parler d'un de mes contacts</p>
            <p className="text-xs text-zinc-500 mt-0.5">Le Doc ouvre avec un bilan (red flags, santé, attachement…).</p>
          </div>
          <ChevronRight size={18} className="text-zinc-500" />
        </button>

        <button
          data-testid="consult-start-topic"
          onClick={startTopic}
          disabled={starting}
          className="w-full flex items-center gap-3 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-pink-500/40 transition-colors p-4 text-left disabled:opacity-60"
        >
          <div className="w-11 h-11 rounded-xl bg-purple-500/15 flex items-center justify-center text-purple-300 shrink-0">
            {starting ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">Un nouveau sujet</p>
            <p className="text-xs text-zinc-500 mt-0.5">Séduction, un crush, un doute… raconte librement.</p>
          </div>
          <ChevronRight size={18} className="text-zinc-500" />
        </button>

        {starting && mode !== "topic" && (
          <p className="text-center text-xs text-pink-400 mt-4 animate-pulse">Le Doc consulte le dossier…</p>
        )}

        <AnimatePresence>
          {startPicker && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4"
              onClick={() => setStartPicker(false)}
            >
              <motion.div
                initial={{ y: 40 }} animate={{ y: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm rounded-3xl bg-zinc-900 border border-zinc-800 p-5 max-h-[70vh] overflow-y-auto"
              >
                <h3 className="font-display text-lg font-black mb-1">Quel contact ?</h3>
                <p className="text-xs text-zinc-500 mb-4">Le Doc démarre par une analyse basée sur ses données.</p>
                {relations.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-6">Analyse d'abord une relation pour en parler ici.</p>
                ) : (
                  <div className="space-y-2">
                    {relations.map((r) => (
                      <button
                        key={r.relationship_id}
                        data-testid={`consult-start-pick-${r.relationship_id}`}
                        onClick={() => startContact(r)}
                        disabled={starting}
                        className="w-full flex items-center gap-3 rounded-xl bg-zinc-950 border border-zinc-800 hover:border-pink-500/40 p-3 text-left disabled:opacity-50"
                      >
                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-pink-500/30 to-purple-500/30 flex items-center justify-center text-pink-300 font-bold shrink-0">
                          {(r.contact_name || "?")[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold block truncate">{r.contact_name}</span>
                          {r.health_score != null && <span className="text-xs text-zinc-500">santé {r.health_score}/100</span>}
                        </div>
                        {starting && <Loader2 size={16} className="animate-spin text-pink-400" />}
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ minHeight: "calc(100vh - 320px)" }}>
      <div className="flex items-center justify-between rounded-2xl bg-zinc-900 border border-zinc-800 px-4 py-2.5 mb-3" data-testid="consult-header">
        <div className="flex items-center gap-2 min-w-0">
          {mode === "contact" ? <Heart size={15} className="text-pink-400 shrink-0" /> : <Sparkles size={15} className="text-purple-300 shrink-0" />}
          <span className="text-sm font-semibold truncate">{mode === "contact" ? relName : "Nouveau sujet"}</span>
        </div>
        <button data-testid="consult-new" onClick={startOver} className="flex items-center gap-1 text-xs font-semibold text-zinc-400 hover:text-white shrink-0">
          <Plus size={14} /> Nouvelle
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto pb-4" data-testid="doctor-conversation">
        {messages.length === 0 && (
          <div className="text-center py-10">
            <div className="w-16 h-16 rounded-2xl bg-pink-500/15 flex items-center justify-center text-pink-400 mx-auto mb-4">
              <Stethoscope size={30} />
            </div>
            <h3 className="font-display text-xl font-black">Parle à ton Docteur Love</h3>
            <p className="text-sm text-zinc-500 mt-2 max-w-xs mx-auto">
              Raconte-lui ta situation à voix haute (ou par écrit). Il t'écoute, t'analyse et te conseille.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                m.role === "user"
                  ? "bg-pink-500 text-white rounded-br-md"
                  : "bg-zinc-800 text-zinc-100 rounded-bl-md"
              } ${m.pending ? "opacity-60 animate-pulse" : ""}`}
            >
              {m.role === "doc" && (
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-pink-400 mb-1">
                  <Stethoscope size={11} /> Docteur
                </div>
              )}
              <p className="leading-relaxed whitespace-pre-wrap">{m.text}</p>
              {m.role === "doc" && m.audio && (
                <button
                  data-testid={`doctor-play-${i}`}
                  onClick={() => playAudio(i, m.audio)}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-pink-300 hover:text-pink-200"
                >
                  {playingIdx === i ? <Pause size={13} /> : <Play size={13} />}
                  {playingIdx === i ? "Pause" : "Écouter"}
                </button>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-zinc-800 rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 size={16} className="animate-spin text-pink-400" />
            </div>
          </div>
        )}

        {cta && !sending && (() => {
          const cRelName = relations.find((r) => r.relationship_id === cta.relationshipId)?.contact_name || relName;
          return (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              data-testid="doctor-plan-offer"
              className="rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/25 p-4"
            >
              <div className="flex items-start gap-2.5">
                <CalendarCheck size={18} className="text-pink-400 shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-200">
                  Tu veux que je te prépare un <span className="font-semibold text-white">plan d'action</span>
                  {cRelName ? <> pour <span className="font-semibold text-white">{cRelName}</span></> : null} ?
                </p>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  data-testid="doctor-plan-offer-yes"
                  onClick={() => createPlan(cta.relationshipId)}
                  disabled={ctaBusy}
                  className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-pink-500 text-white text-sm font-bold py-2.5 hover:bg-pink-600 disabled:opacity-60"
                >
                  {ctaBusy ? <Loader2 size={14} className="animate-spin" /> : <CalendarCheck size={14} />}
                  Oui, prépare mon plan
                </button>
                <button
                  data-testid="doctor-plan-offer-no"
                  onClick={() => setCta(null)}
                  className="rounded-full border border-zinc-700 text-zinc-400 text-sm font-semibold px-4 hover:text-zinc-200"
                >
                  Plus tard
                </button>
              </div>
            </motion.div>
          );
        })()}
      </div>

      <div className="sticky bottom-0 pt-2 bg-[#09090b]/80 backdrop-blur z-30">
        <form onSubmit={submitText} className="flex items-center gap-2">
          <input
            data-testid="doctor-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Écris ou enregistre ta situation…"
            disabled={recording || sending}
            className="flex-1 rounded-full bg-zinc-900 border border-zinc-800 focus:border-pink-500 px-4 py-3 text-sm focus:outline-none"
          />
          {text.trim() ? (
            <button
              data-testid="doctor-send-text"
              type="submit"
              disabled={sending}
              className="w-12 h-12 rounded-full bg-pink-500 text-white flex items-center justify-center hover:bg-pink-600 disabled:opacity-50 shrink-0"
            >
              <Send size={18} />
            </button>
          ) : (
            <button
              data-testid="doctor-record-btn"
              type="button"
              onClick={recording ? stopRec : startRec}
              disabled={sending}
              className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                recording ? "bg-red-500 text-white animate-pulse" : "bg-pink-500 text-white hover:bg-pink-600"
              } disabled:opacity-50`}
            >
              {recording ? <Square size={18} /> : <Mic size={20} />}
            </button>
          )}
        </form>
        {recording && (
          <p className="text-center text-xs text-red-400 mt-2 animate-pulse">● Enregistrement… appuie sur ⏹ pour envoyer</p>
        )}
      </div>

      <AnimatePresence>
        {pickerOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setPickerOpen(false)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-3xl bg-zinc-900 border border-zinc-800 p-5 max-h-[70vh] overflow-y-auto"
            >
              <h3 className="font-display text-lg font-black mb-1">Pour quelle relation ?</h3>
              <p className="text-xs text-zinc-500 mb-4">Le Doc crée un plan basé sur son analyse.</p>
              {relations.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-6">Analyse d'abord une relation.</p>
              ) : (
                <div className="space-y-2">
                  {relations.map((r) => (
                    <button
                      key={r.relationship_id}
                      data-testid={`consult-pick-rel-${r.relationship_id}`}
                      onClick={() => createPlan(r.relationship_id)}
                      disabled={ctaBusy}
                      className="w-full flex items-center gap-3 rounded-xl bg-zinc-950 border border-zinc-800 hover:border-pink-500/40 p-3 text-left disabled:opacity-50"
                    >
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-pink-500/30 to-purple-500/30 flex items-center justify-center text-pink-300 font-bold shrink-0">
                        {(r.contact_name || "?")[0].toUpperCase()}
                      </div>
                      <span className="flex-1 text-sm font-semibold">{r.contact_name}</span>
                      {ctaBusy && <Loader2 size={16} className="animate-spin text-pink-400" />}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------- Action plans ----------------

function Plans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [relations, setRelations] = useState([]);
  const [picker, setPicker] = useState(false);
  const [creating, setCreating] = useState(null);
  const [active, setActive] = useState(null);
  const [checkinFor, setCheckinFor] = useState(null);
  const [note, setNote] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  const load = () => api.get("/doctor/plans").then((r) => setPlans(r.data.plans)).catch(() => setPlans([]));
  useEffect(() => {
    load();
    api.get("/relationships").then((r) => setRelations(r.data)).catch(() => {});
  }, []);

  const createPlan = async (rid) => {
    setCreating(rid); vibrate();
    try {
      const { data } = await api.post("/doctor/plan", { relationship_id: rid });
      toast.success("Plan d'action créé");
      setPicker(false);
      await load();
      setActive(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    } finally { setCreating(null); }
  };

  const toggleTask = async (plan, week, idx, done) => {
    setActive((p) => {
      const w = p.weeks.find((x) => x.week === week);
      if (w) w.tasks[idx].done = done;
      return { ...p };
    });
    try {
      const { data } = await api.post(`/doctor/plan/${plan.plan_id}/task`, { week, task_index: idx, done });
      setActive(data); load();
    } catch { toast.error("Erreur"); }
  };

  const doCheckin = async () => {
    vibrate();
    try {
      await api.post(`/doctor/plan/${checkinFor.plan_id}/checkin`, { note });
      toast.success("Check-in enregistré 💜");
      setCheckinFor(null); setNote("");
      const { data } = await api.get(`/doctor/plan/${checkinFor.plan_id}`);
      setActive(data); load();
    } catch { toast.error("Erreur"); }
  };

  const removePlan = async (pid) => {
    if (!window.confirm("Supprimer ce plan ?")) return;
    await api.delete(`/doctor/plan/${pid}`);
    setActive(null); load();
  };

  const regeneratePlan = async () => {
    if (!window.confirm("Régénérer la suite du plan ? Tes tâches déjà cochées sont conservées.")) return;
    setRegenerating(true); vibrate();
    try {
      const { data } = await api.post(`/doctor/plan/${active.plan_id}/regenerate`);
      setActive(data); load();
      toast.success("Le Doc a mis à jour ton plan 💜");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Régénération impossible");
    } finally { setRegenerating(false); }
  };

  if (active) {
    const pct = active.progress?.total ? Math.round((active.progress.done / active.progress.total) * 100) : 0;
    return (
      <div className="space-y-4" data-testid="plan-detail">
        <button onClick={() => setActive(null)} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white">
          <ArrowLeft size={16} /> Mes plans
        </button>
        <div className="rounded-2xl bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/20 p-5">
          <p className="text-xs uppercase tracking-wider text-pink-400 font-bold">Plan d'action · {active.contact_name}</p>
          <p className="text-sm text-zinc-300 mt-2">{active.intro}</p>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-pink-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs font-bold text-pink-400">{pct}%</span>
          </div>
        </div>

        <button
          data-testid="plan-regenerate"
          onClick={regeneratePlan}
          disabled={regenerating}
          className="w-full flex items-center justify-center gap-2 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-pink-500/40 transition-colors text-sm font-semibold py-3 disabled:opacity-60"
        >
          {regenerating ? <Loader2 size={15} className="animate-spin text-pink-400" /> : <RefreshCw size={15} className="text-pink-400" />}
          {regenerating ? "Le Doc réécrit la suite…" : "Régénérer la suite (garde ma progression)"}
        </button>

        {active.checkin_due && (
          <button
            data-testid="plan-checkin-cta"
            onClick={() => setCheckinFor(active)}
            className="w-full flex items-center gap-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 p-4 text-left"
          >
            <Bell className="text-amber-400 shrink-0" size={18} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-200">C'est l'heure de ton check-in</p>
              <p className="text-xs text-amber-300/70">Fais le point avec le Doc sur tes progrès.</p>
            </div>
            <ChevronRight size={16} className="text-amber-400" />
          </button>
        )}

        {active.weeks.map((w) => (
          <div key={w.week} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
            <p className="text-sm font-bold text-white">Semaine {w.week} · {w.focus}</p>
            <div className="mt-3 space-y-2">
              {w.tasks.map((t, i) => (
                <button
                  key={i}
                  data-testid={`plan-task-${w.week}-${i}`}
                  onClick={() => toggleTask(active, w.week, i, !t.done)}
                  className="w-full flex items-start gap-2.5 text-left"
                >
                  {t.done ? <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" /> : <Circle size={18} className="text-zinc-600 shrink-0 mt-0.5" />}
                  <span className={`text-sm ${t.done ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{t.text}</span>
                </button>
              ))}
            </div>
          </div>
        ))}

        <button onClick={() => removePlan(active.plan_id)} className="w-full text-xs text-zinc-500 hover:text-red-400 py-2 flex items-center justify-center gap-1">
          <Trash2 size={13} /> Supprimer ce plan
        </button>

        <AnimatePresence>
          {checkinFor && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4"
              onClick={() => setCheckinFor(null)}
            >
              <motion.div
                initial={{ y: 40 }} animate={{ y: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm rounded-3xl bg-zinc-900 border border-pink-500/25 p-6"
              >
                <h3 className="font-display text-xl font-black">Ton check-in</h3>
                <p className="text-sm text-zinc-400 mt-1">Comment ça s'est passé cette semaine avec {checkinFor.contact_name} ?</p>
                <textarea
                  data-testid="checkin-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Ce qui a avancé, ce qui bloque…"
                  className="mt-3 w-full rounded-xl bg-zinc-950 border border-zinc-800 focus:border-pink-500 p-3 text-sm focus:outline-none"
                />
                <button
                  data-testid="checkin-submit"
                  onClick={doCheckin}
                  className="mt-3 w-full rounded-full bg-pink-500 text-white font-bold py-3 hover:bg-pink-600"
                >
                  Valider mon check-in
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="plans-list">
      <button
        data-testid="create-plan-btn"
        onClick={() => { vibrate(); setPicker(true); }}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-3.5 hover:bg-pink-600 glow-pink"
      >
        <Plus size={17} /> Nouveau plan d'action
      </button>

      {plans === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-pink-400" /></div>
      ) : plans.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 p-8 text-center">
          <CalendarCheck className="mx-auto text-zinc-600 mb-2" size={24} />
          <p className="text-sm text-zinc-500">Aucun plan d'action.</p>
          <p className="text-xs text-zinc-600 mt-1">Crée un plan sur-mesure pour une de tes relations 👆</p>
        </div>
      ) : (
        plans.map((p) => (
          <button
            key={p.plan_id}
            data-testid={`plan-${p.plan_id}`}
            onClick={() => api.get(`/doctor/plan/${p.plan_id}`).then((r) => setActive(r.data))}
            className="w-full flex items-center gap-3 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-pink-500/40 p-4 text-left"
          >
            <div className="w-10 h-10 rounded-xl bg-pink-500/15 flex items-center justify-center text-pink-400 shrink-0">
              <CalendarCheck size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold flex items-center gap-2">
                {p.contact_name}
                {p.checkin_due && <span className="text-[9px] font-bold text-amber-300 bg-amber-500/15 rounded-full px-1.5 py-0.5">CHECK-IN</span>}
              </p>
              <p className="text-xs text-zinc-500">{p.weeks?.length} semaines · {p.progress?.done}/{p.progress?.total} tâches</p>
            </div>
            <ChevronRight size={18} className="text-zinc-500" />
          </button>
        ))
      )}

      <AnimatePresence>
        {picker && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setPicker(false)}
          >
            <motion.div
              initial={{ y: 40 }} animate={{ y: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-3xl bg-zinc-900 border border-zinc-800 p-5 max-h-[70vh] overflow-y-auto"
            >
              <h3 className="font-display text-lg font-black mb-1">Pour quelle relation ?</h3>
              <p className="text-xs text-zinc-500 mb-4">Le Doc crée un plan basé sur son analyse.</p>
              {relations.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-6">Analyse d'abord une relation.</p>
              ) : (
                <div className="space-y-2">
                  {relations.map((r) => (
                    <button
                      key={r.relationship_id}
                      data-testid={`pick-rel-${r.relationship_id}`}
                      onClick={() => createPlan(r.relationship_id)}
                      disabled={creating}
                      className="w-full flex items-center gap-3 rounded-xl bg-zinc-950 border border-zinc-800 hover:border-pink-500/40 p-3 text-left disabled:opacity-50"
                    >
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-pink-500/30 to-purple-500/30 flex items-center justify-center text-pink-300 font-bold shrink-0">
                        {(r.contact_name || "?")[0].toUpperCase()}
                      </div>
                      <span className="flex-1 text-sm font-semibold">{r.contact_name}</span>
                      {creating === r.relationship_id && <Loader2 size={16} className="animate-spin text-pink-400" />}
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------- Page shell (with tier gate) ----------------

export default function Doctor() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("consult");
  const [locked, setLocked] = useState(null); // null=loading, true/false

  useEffect(() => {
    api.get("/doctor/plans")
      .then(() => setLocked(false))
      .catch((e) => setLocked(e?.response?.status === 402 ? true : false));
  }, []);

  if (locked === null) {
    return <Layout><div className="flex justify-center py-24"><Loader2 className="animate-spin text-pink-400" /></div></Layout>;
  }

  if (locked) {
    return (
      <Layout>
        <div className="text-center py-16" data-testid="doctor-locked">
          <div className="w-16 h-16 rounded-2xl bg-pink-500/15 flex items-center justify-center text-pink-400 mx-auto mb-5">
            <Stethoscope size={30} />
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight">Docteur Love</h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-xs mx-auto">
            Consultations vocales avec ton coach IA & plans d'action sur-mesure. Réservé à l'offre <span className="text-pink-400 font-semibold">Docteur</span>.
          </p>
          <ul className="text-sm text-zinc-300 mt-6 space-y-2 max-w-xs mx-auto text-left">
            <li className="flex items-center gap-2"><Sparkles size={15} className="text-pink-400" /> Parle à voix haute, il te répond en vocal</li>
            <li className="flex items-center gap-2"><CalendarCheck size={15} className="text-pink-400" /> Plans d'action multi-semaines</li>
            <li className="flex items-center gap-2"><Bell size={15} className="text-pink-400" /> Check-ins pour suivre tes progrès</li>
          </ul>
          <button
            data-testid="doctor-upsell"
            onClick={() => { vibrate(); navigate("/pricing"); }}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-pink-500 text-white font-bold py-4 px-8 hover:bg-pink-600 glow-pink"
          >
            <Crown size={18} /> Débloquer Docteur
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-4">
        <h1 className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
          <Stethoscope className="text-pink-400" size={24} /> Docteur Love
        </h1>
      </div>
      <div className="flex gap-2 mb-5 p-1 rounded-full bg-zinc-900 border border-zinc-800">
        {[["consult", "Consultation"], ["plans", "Plans d'action"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`doctor-tab-${k}`}
            onClick={() => { vibrate(); setTab(k); }}
            className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
              tab === k ? "bg-pink-500 text-white" : "text-zinc-400"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "consult" ? <Consultation goToPlans={() => setTab("plans")} /> : <Plans />}
    </Layout>
  );
}
